import React from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { removeBasePath } from '@bonnet/next/base-path';

import { sendClick } from 'reaxl-analytics';

import SearchLinkModule from '@/modules/SearchLinkModule';

import BackLink from '@/components/BackLink';

/**
 * SearchLink component renders a link to a Search Results Page (SRP) based on the current listing or request.
 * This component is primarily used on Vehicle Detail Pages (VDP) when the user arrives from external sources
 * such as emails, non-findcar applications, or direct links where no previous SRP navigation context exists.
 *
 * The component conditionally renders based on the availability of a search link in the Redux store.
 * If no search link is available, the component returns null to maintain layout consistency.
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS class name(s) to apply to the search link
 * @returns {React.ReactElement|null} BackLink component with search functionality or null if no search link available
 */
export function SearchLink({
    className,
}) {
    const { link: searchLinkHref, label: searchLinkLabel } = useSelector(SearchLinkModule.duck.selectors.getDuckState);

    // return empty element to preserve layout
    if (!searchLinkHref) {
        return null;
    }

    const handleSearchLinkClick = (event) => {
        sendClick('searchLinkClick', event);
    };

    return (
        <BackLink
            data-cmp="searchLink"
            href={removeBasePath(searchLinkHref)}
            className={className}
            onClick={handleSearchLinkClick}
        >
            {searchLinkLabel}
        </BackLink>
    );
}

SearchLink.propTypes = {
    /**
     * Additional CSS class name(s) to apply to the search link.
     * These classes will be passed through to the underlying BackLink component.
     * Can be used to customize the appearance of the search link.
     */
    className: PropTypes.string,
};

export default React.memo(SearchLink);
